"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { MailCheckIcon, MailIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod/v4";
import { Alert, AlertTitle } from "../alert";
import { Button } from "../button";
import { TextareaField, TextField } from "../field-types";
import { Form, FormField, FormItem } from "../form";

const contactFormSchema = z.object({
	email: z.email(),
	name: z.string().min(3),
	message: z.string().min(10),
});

export interface MarketingContactFormValues {
	name: string;
	email: string;
	message: string;
}

export interface MarketingContactFormStrings {
	nameLabel: string;
	emailLabel: string;
	messageLabel: string;
	submitLabel: string;
	successMessage: string;
	errorMessage: string;
}

export interface MarketingContactFormProps {
	strings: MarketingContactFormStrings;
	onSubmit: (values: MarketingContactFormValues) => Promise<void>;
}

export function MarketingContactForm({
	strings,
	onSubmit,
}: MarketingContactFormProps) {
	const form = useForm<MarketingContactFormValues>({
		resolver: zodResolver(contactFormSchema),
		defaultValues: {
			name: "",
			email: "",
			message: "",
		},
	});

	const submit = form.handleSubmit(async (values) => {
		try {
			await onSubmit(values);
		} catch {
			form.setError("root", { message: strings.errorMessage });
		}
	});

	return (
		<div>
			{form.formState.isSubmitSuccessful ? (
				<Alert variant="success">
					<MailCheckIcon />
					<AlertTitle>{strings.successMessage}</AlertTitle>
				</Alert>
			) : (
				<Form {...form}>
					<form
						onSubmit={submit}
						className="flex flex-col items-stretch gap-6"
					>
						{form.formState.errors.root?.message && (
							<Alert variant="error">
								<MailIcon />
								<AlertTitle>
									{form.formState.errors.root.message}
								</AlertTitle>
							</Alert>
						)}

						<FormField
							control={form.control}
							name="name"
							render={({ field, fieldState }) => (
								<FormItem>
									<TextField
										id="contact-name"
										label={strings.nameLabel}
										value={field.value}
										onChange={field.onChange}
										onBlur={field.onBlur}
										name={field.name}
										error={fieldState.error?.message}
									/>
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="email"
							render={({ field, fieldState }) => (
								<FormItem>
									<TextField
										id="contact-email"
										label={strings.emailLabel}
										value={field.value}
										onChange={field.onChange}
										onBlur={field.onBlur}
										name={field.name}
										autoComplete="email"
										error={fieldState.error?.message}
									/>
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="message"
							render={({ field, fieldState }) => (
								<FormItem>
									<TextareaField
										id="contact-message"
										label={strings.messageLabel}
										value={field.value}
										onChange={field.onChange}
										onBlur={field.onBlur}
										name={field.name}
										error={fieldState.error?.message}
									/>
								</FormItem>
							)}
						/>

						<Button
							type="submit"
							className="w-full"
							variant="primary"
							loading={form.formState.isSubmitting}
						>
							{strings.submitLabel}
						</Button>
					</form>
				</Form>
			)}
		</div>
	);
}
